import { useEffect, useRef } from 'react';

import { useCartStore, useStaffSessionStore } from '../store';
import {
    clearCartForStaff,
    getCartForStaff,
    saveCartForStaff,
} from './cart-persistence';

import type { Cart } from '../types/cart';

const PERSIST_DEBOUNCE_MS = 300;

function isEmptyCart(cart: Partial<Cart>) {
    return Object.keys(cart).length === 0;
}

export function CartPersistenceManager() {
    const staffId = useStaffSessionStore((state) => state.staffId);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined,
    );

    useEffect(() => {
        if (!staffId) {
            return;
        }

        if (!isEmptyCart(useCartStore.getState().cart)) {
            return;
        }

        getCartForStaff(staffId).then((savedCart) => {
            if (savedCart && !isEmptyCart(savedCart)) {
                useCartStore.getState().updateCart(savedCart);
            }
        });
    }, [staffId]);

    useEffect(() => {
        const unsubscribe = useCartStore.subscribe((state, prevState) => {
            if (state.cart === prevState.cart) {
                return;
            }

            const staffId = useStaffSessionStore.getState().staffId;

            if (!staffId) {
                return;
            }

            if (isEmptyCart(state.cart)) {
                clearCartForStaff(staffId);
                return;
            }

            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                saveCartForStaff(staffId, state.cart);
            }, PERSIST_DEBOUNCE_MS);
        });

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            unsubscribe();
        };
    }, []);

    return null;
}
